import React from 'react'
import { CSSTransition } from 'react-transition-group'
import cx from 'classnames'

import { Backdrop } from 'components/shared/Backdrop/Backdrop'
import { TetherPortal } from 'components/shared/TetherPortal/TetherPortal'
import { Content } from './common/Content/Content'
import { Footer } from './common/Footer/Footer'
import { Header } from './common/Header/Header'

import { ModalHandlersContext, ModalPropsContext } from './context'

import styles from './Modal.module.scss'

export const Modal = ({
  children,
  portalClassName = undefined,
  className = undefined,
  backLabel,
  cancelLabel,
  confirmIcon,
  confirmLabel,
  title,
  onBack,
  onCancel,
  onClose,
  onConfirm,
  isConfirmDisabled,
  isOpen,
  nestedModalEscapeCapture = false,
}) => {
  const ref = React.useRef()

  const propsContext = React.useMemo(
    () => ({
      backLabel,
      cancelLabel,
      confirmIcon,
      confirmLabel,
      title,
      isConfirmDisabled,
      isOpen,
    }),
    [
      backLabel,
      cancelLabel,
      confirmIcon,
      confirmLabel,
      title,
      isConfirmDisabled,
      isOpen,
    ],
  )

  const handlersContext = React.useMemo(
    () => ({
      onBack,
      onCancel,
      onClose,
      onConfirm,
    }),
    [onBack, onCancel, onClose, onConfirm],
  )

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = 'initial'
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) return

    const handleEscKey = event => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return

      if (nestedModalEscapeCapture) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }

      onClose()
    }

    const options = nestedModalEscapeCapture ? { capture: true } : false

    document.addEventListener('keydown', handleEscKey, options)

    return () => {
      document.removeEventListener('keydown', handleEscKey, options)
    }
  }, [onClose, isOpen, nestedModalEscapeCapture])

  return (
    <ModalPropsContext.Provider value={propsContext}>
      <ModalHandlersContext.Provider value={handlersContext}>
        {/* TODO: resolve enter animation since it doesn't
        fire at all unlike exit animation */}
        <TetherPortal {...{ isOpen }}>
          <CSSTransition
            classNames={{
              enter: styles.enter,
              enterActive: styles.enterActive,
              exit: styles.exit,
              exitActive: styles.exitActive,
            }}
            nodeRef={ref}
            timeout={200}
            in={isOpen}
            appear
            mountOnEnter
            unmountOnExit
          >
            <div
              className={cx(styles.portal, {
                [portalClassName]: Boolean(portalClassName),
              })}
            >
              <Backdrop isVisible={isOpen} />
              <div
                aria-modal="true"
                aria-labelledby={title}
                className={cx(styles.content, {
                  [className]: Boolean(className),
                })}
                ref={ref}
                role="dialog"
              >
                {children}
              </div>
            </div>
          </CSSTransition>
        </TetherPortal>
      </ModalHandlersContext.Provider>
    </ModalPropsContext.Provider>
  )
}

Modal.Content = Content
Modal.Header = Header
Modal.Footer = Footer
